import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ExternalLink,
  Gift,
  Loader2,
  Lock,
  LucideIcon,
  Pencil,
  TriangleAlert,
} from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { BRAND } from "~/brand";
import { LoadingScreen } from "~/components/LoadingScreen";
import { VolumeLadderDialog } from "~/components/contract-term/VolumeLadderDialog";
import { useContractFlow, useExtendFlow } from "~/components/machtiging-ondertekenen";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "~/components/ui/form";
import { HelpTooltip } from "~/components/ui/help-tooltip";
import { Input } from "~/components/ui/input";
import { CODE_MAX_LENGTH, useCodeClaim } from "~/hooks/use-code-claim";
import {
  contractTypesQueryOptions,
  currentContractQueryOptions,
  profileQueryOptions,
  profileUpdateMutationOptions,
  type VolumeTier,
} from "~/lib/api";
import {
  deriveContractTerm,
  formatKwh,
  getVolumeSettlementNote,
  pendingMandateStartYear,
  ratePercentages,
  tierForKwh,
  VOLUME_DISCOUNT_INTRO,
} from "~/lib/contract-term";
import { formatPhone } from "~/lib/format";
import {
  useExtendOption,
  useHasAvailableTerms,
  useYearToDateKwh,
} from "~/lib/use-contract";
import { REFERRAL_ENABLED } from "~/lib/viral-loops";

export const Route = createFileRoute("/(authenticated)/_app/mijn-gegevens/")({
  validateSearch: z.object({
    verlengen: z.boolean().optional().catch(undefined),
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(profileQueryOptions()),
      context.queryClient.ensureQueryData(currentContractQueryOptions()),
      context.queryClient.ensureQueryData(contractTypesQueryOptions()),
    ]);
  },
  pendingComponent: LoadingScreen,
  component: MijnGegevens,
});

function val(value?: string | null) {
  return value && value.trim() ? value : "—";
}

const phoneSchema = z
  .string()
  .transform((raw) => raw.replace(/[\s-]/g, ""))
  .refine(
    (value) => value === "" || /^(?:\+31|0031|0)[1-9]\d{8}$/.test(value),
    "Ongeldig telefoonnummer",
  );

const ibanSchema = z
  .string()
  .transform((raw) => raw.replace(/\s/g, "").toUpperCase())
  .refine(
    (value) => value === "" || /^NL\d{2}[A-Z]{4}\d{10}$/.test(value),
    "Ongeldig Nederlands IBAN",
  );

type EditableField = "phoneNumber" | "iban";

function normalizeUpdateErrorMessage(message?: string) {
  if (!message) return "Er ging iets mis. Probeer het opnieuw.";
  if (message.toLowerCase().includes("failed to fetch")) {
    return "We konden geen verbinding maken. Controleer je internetverbinding en probeer het opnieuw.";
  }
  return message;
}

function useEditableProfileField(
  field: EditableField,
  value: string,
  {
    schema,
    inputType,
    placeholder,
    format = (raw: string) => raw,
  }: {
    schema: z.ZodType<string, string>;
    inputType: string;
    placeholder: string;
    format?: (raw: string) => string;
  },
) {
  const initialValue = format(value);
  const [isEditing, setIsEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation(profileUpdateMutationOptions());

  const form = useForm<{ value: string }>({
    resolver: zodResolver(z.object({ value: schema })),
    defaultValues: { value: initialValue },
  });

  const start = () => {
    form.reset({ value: initialValue });
    setErrorMessage("");
    setIsEditing(true);
  };

  const cancel = () => {
    form.reset({ value: initialValue });
    setErrorMessage("");
    setIsEditing(false);
  };

  const save = form.handleSubmit(async (data) => {
    setErrorMessage("");

    const initial = schema.safeParse(initialValue);
    if (initial.success && data.value === initial.data) {
      setIsEditing(false);
      return;
    }
    try {
      await mutateAsync({ [field]: data.value });
      await queryClient.invalidateQueries({ queryKey: ["profile", "current"] });
      setIsEditing(false);
    } catch (error) {
      setErrorMessage(
        normalizeUpdateErrorMessage(error instanceof Error ? error.message : undefined),
      );
    }
  });

  return {
    isEditing,
    start,
    cancel,
    save,
    form,
    isPending,
    errorMessage,
    inputType,
    placeholder,
  };
}
type EditableProfileField = ReturnType<typeof useEditableProfileField>;

function EditSectionActions({ edit }: { edit: EditableProfileField }) {
  if (!edit.isEditing) {
    return (
      <Button variant="outline" size="sm" className="rounded-full" onClick={edit.start}>
        <Pencil className="size-3.5" />
        Bewerken
      </Button>
    );
  }

  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" className="rounded-full" onClick={edit.cancel}>
        Annuleren
      </Button>
      <Button
        variant="highlight"
        size="sm"
        className="rounded-full"
        onClick={edit.save}
        disabled={edit.isPending}
      >
        {edit.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
        Opslaan
      </Button>
    </div>
  );
}

function EditableFieldInput({ edit }: { edit: EditableProfileField }) {
  return (
    <Form {...edit.form}>
      <FormField
        control={edit.form.control}
        name="value"
        render={({ field }) => (
          <FormItem className="w-full gap-1">
            <FormControl>
              <Input
                inputSize="sm"
                type={edit.inputType}
                placeholder={edit.placeholder}
                className="px-4"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </Form>
  );
}

function Section({
  title,
  action,
  notice,
  noticeIcon: NoticeIcon = TriangleAlert,
  noticeVariant = "warning",
  children,
}: {
  title: string;
  action?: ReactNode;
  notice?: ReactNode;
  noticeIcon?: LucideIcon | null;
  noticeVariant?: "warning" | "muted";
  children: ReactNode;
}) {
  return (
    <section className="mb-8 overflow-hidden border">
      <div className="flex items-center justify-between px-4 py-4 lg:px-6 lg:py-5">
        <h2 className="text-xl font-medium lg:text-2xl">{title}</h2>
        {action ? <div className="hidden lg:flex">{action}</div> : null}
      </div>
      {notice ? (
        <div className="border-t px-4 py-3 lg:px-6">
          <Alert variant={noticeVariant}>
            {NoticeIcon ? (
              <NoticeIcon
                className={
                  noticeVariant === "warning"
                    ? "text-status-warning"
                    : "text-muted-foreground"
                }
              />
            ) : null}
            <AlertDescription className="text-xs lg:text-sm">{notice}</AlertDescription>
          </Alert>
        </div>
      ) : null}
      <dl className="divide-y border-t">{children}</dl>
    </section>
  );
}

function Row({
  label,
  hint,
  children,
  action,
  locked,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  locked?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 px-4 py-4 lg:flex-row lg:items-center lg:gap-4 lg:px-6 lg:py-6">
      <dt className="flex w-full shrink-0 items-center gap-1.5 text-xs font-semibold lg:w-2/5 lg:min-w-40 lg:text-sm">
        {label}
        {hint ? <HelpTooltip content={hint} iconClassName="size-4 opacity-50" /> : null}
      </dt>
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
        <dd className="text-muted-foreground flex min-w-0 flex-1 items-center gap-1.5 text-xs lg:text-sm">
          {children}
          {locked ? <Lock className="size-3 shrink-0 opacity-40" /> : null}
        </dd>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </div>
  );
}

function ActionButton({
  to,
  onClick,
  variant = "outline",
  children,
}: {
  to?: string;
  onClick?: () => void;
  variant?: ComponentProps<typeof Button>["variant"];
  children: ReactNode;
}) {
  return (
    <Button
      asChild={!!to}
      variant={variant}
      size="sm"
      className="rounded-full"
      onClick={onClick}
    >
      {to ? <Link to={to}>{children}</Link> : children}
    </Button>
  );
}

/** "11 dagen" / "1 dag". Rounds up, so the last partial day is not "0 dagen". */
function daysLeftLabel(deadline: string) {
  const days = Math.max(
    0,
    Math.ceil((new Date(deadline).getTime() - Date.now()) / 86_400_000),
  );
  return days === 1 ? "1 dag" : `${days} dagen`;
}

/**
 * The one code a customer can hold (issue #2086): a friend's, a promo or a
 * partner's code, claimed here or at sign-up. Once one is on record it is shown
 * read-only in place of the field; for a promo code that includes the auditable
 * record (promise, and whether the bonus has been applied yet).
 */
function CodeSection() {
  const { claimed, canClaim, label, placeholder, referralDeadline, claimCode } =
    useCodeClaim();
  const [code, setCode] = useState("");
  const {
    mutate: apply,
    isPending,
    data: result,
  } = useMutation({ mutationFn: claimCode });

  const action = REFERRAL_ENABLED ? (
    <Button asChild variant="outline" size="sm" className="rounded-full">
      <Link to="/vriendenvoordeel">
        <Gift aria-hidden="true" />
        Deel je eigen code
      </Link>
    </Button>
  ) : undefined;

  if (claimed) {
    return (
      <Section title="Gebruikte code" action={action}>
        <Row label="Code">{claimed.code}</Row>
        {claimed.kind === "referral" && <Row label="Programma">Vriendenvoordeel</Row>}
        {claimed.kind === "partner" && claimed.partnerName && (
          <Row label="Partner">{claimed.partnerName}</Row>
        )}
        {claimed.kind === "promo" && (
          <>
            <Row label="Programma">{claimed.redemption.campaignName}</Row>
            {/* The promise text is written per code and already names the amount. */}
            <Row label="Belofte">{claimed.redemption.promiseText}</Row>
            <Row label="Status">
              {claimed.redemption.payoutStatus === "applied" ? (
                <Badge className="bg-status-success/15 text-status-success-strong hover:bg-status-success/15 gap-1.5 font-normal">
                  <span className="bg-status-success size-1.5 rounded-full" />
                  Toegepast
                </Badge>
              ) : (
                "Wordt verwerkt bij je eerste uitbetaling"
              )}
            </Row>
          </>
        )}
      </Section>
    );
  }

  if (!canClaim) return null;

  const trimmedCode = code.trim();

  const errorText =
    result?.status === "rejected"
      ? result.message
      : result?.status === "unknown"
        ? normalizeUpdateErrorMessage()
        : null;

  const notice = errorText
    ? { text: errorText, icon: TriangleAlert, variant: "warning" as const }
    : referralDeadline
      ? {
          // A promo code stays open after this, so the deadline is the friend's code's alone.
          text: (
            <p>
              Nog <strong>{daysLeftLabel(referralDeadline)}</strong> om de code van een
              vriend in te vullen.
            </p>
          ),
          icon: null,
          variant: "muted" as const,
        }
      : null;

  return (
    <Section
      title={label}
      action={action}
      notice={notice?.text}
      noticeIcon={notice?.icon}
      noticeVariant={notice?.variant}
    >
      <Row label="Code">
        <div className="flex w-full flex-col items-stretch gap-3 sm:flex-row">
          <Input
            inputSize="sm"
            className="w-full px-4 sm:min-w-0 sm:flex-1"
            maxLength={CODE_MAX_LENGTH}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder={placeholder}
            disabled={isPending}
          />
          <Button
            variant="highlight"
            className="h-auto w-full rounded-full sm:w-auto"
            disabled={!trimmedCode || isPending}
            onClick={() => apply(trimmedCode)}
          >
            {isPending ? <Loader2 className="size-4 animate-spin" /> : "Toepassen"}
          </Button>
        </div>
      </Row>
    </Section>
  );
}

/** The tier reached with the year-to-date volume, and the distance to the next one. */
function VolumeTierStatus({
  baseRate,
  tiers,
  ytdKwh,
}: {
  baseRate: number;
  tiers: VolumeTier[];
  ytdKwh?: number;
}) {
  if (ytdKwh === undefined) {
    const lowestRate = tiers.at(-1)?.commissionRate ?? baseRate;

    return (
      <span>
        {ratePercentages(baseRate).commission}% – {ratePercentages(lowestRate).commission}
        %
      </span>
    );
  }

  const { commissionRate, next } = tierForKwh(baseRate, tiers, ytdKwh);

  return (
    <>
      <span>
        {ratePercentages(commissionRate).commission}% bij {formatKwh(ytdKwh)} dit jaar
      </span>
      {next && (
        <span className="text-muted-foreground text-sm">
          Nog {formatKwh(next.fromKwh - ytdKwh)} tot{" "}
          {ratePercentages(next.commissionRate).commission}%
        </span>
      )}
    </>
  );
}

function MijnGegevens() {
  const { data: profileData } = useSuspenseQuery(profileQueryOptions());
  const { data: mandateData } = useSuspenseQuery(currentContractQueryOptions());
  const mandate = mandateData.mandate;
  const ytdKwh = useYearToDateKwh();
  const openContractFlow = useContractFlow();
  const openExtendFlow = useExtendFlow();
  const p = profileData?.profile;

  const phoneEdit = useEditableProfileField("phoneNumber", p?.phoneNumber ?? "", {
    schema: phoneSchema,
    inputType: "tel",
    placeholder: "06 12 34 56 78",
    format: (raw) => (raw ? formatPhone(raw) : ""),
  });
  const ibanEdit = useEditableProfileField("iban", p?.iban ?? "", {
    schema: ibanSchema,
    inputType: "text",
    placeholder: "NL12 ABCD 0123 4567 89",
  });

  const fullName = [p?.initials, p?.firstName, p?.lastName].filter(Boolean).join(" ");
  const street = [p?.street, p?.houseNumber].filter(Boolean).join(" ");
  const address = [p?.addition ? `${street} ${p.addition}` : street, p?.zipcode, p?.city]
    .filter((part) => part && part.trim())
    .join(", ");
  const partner = p?.partner?.trim();
  const term = deriveContractTerm(mandate);
  const upcoming = mandateData.upcoming ?? null;
  const upcomingTerm = deriveContractTerm(upcoming);
  const futureStartYear = pendingMandateStartYear(mandate, upcoming);
  const pending = mandateData.pending;
  const pendingTerm = deriveContractTerm(pending);
  const hasAvailableTerms = useHasAvailableTerms();
  const showSignCta = !mandate && !pending && hasAvailableTerms;
  const extend = useExtendOption();
  const openMachtiging = () => openContractFlow({ step: "looptijd" });
  const payoutLabel =
    p?.payoutFrequency === "quarterly"
      ? "Kwartaal"
      : p?.payoutFrequency === "yearly"
        ? "Jaar"
        : null;

  const phoneNotice = phoneEdit.errorMessage
    ? { text: phoneEdit.errorMessage, icon: TriangleAlert, variant: "warning" as const }
    : phoneEdit.isEditing
      ? {
          text: (
            <p>
              Alleen je telefoonnummer kun je hier zelf wijzigen. Andere gegevens zijn
              onderdeel van je machtiging, dus neem contact op met onze{" "}
              <a
                className="cursor-pointer underline underline-offset-4"
                onClick={() => window.$crisp?.push(["do", "chat:open"])}
              >
                klantenservice
              </a>{" "}
              als deze aangepast dienen te worden.
            </p>
          ),
          icon: Lock,
          variant: "muted" as const,
        }
      : null;

  const hasSignedMandate = Boolean(mandate?.active);
  const mandateInForce = Boolean(hasSignedMandate && mandate?.hasStarted);
  const mandateStartsNextYear = futureStartYear !== undefined;
  // Frequency can be set before a next-year term starts; payouts unlock on hasStarted.
  const showPayoutSetup = hasSignedMandate && !payoutLabel;
  const ibanNotice = ibanEdit.errorMessage
    ? { text: ibanEdit.errorMessage, icon: TriangleAlert, variant: "warning" as const }
    : ibanEdit.isEditing
      ? {
          text: (
            <p>
              Alleen je IBAN-nummer kun je hier zelf wijzigen. Andere gegevens zijn
              onderdeel van je machtiging, dus neem contact op met onze{" "}
              <a
                className="cursor-pointer underline underline-offset-4"
                onClick={() => window.$crisp?.push(["do", "chat:open"])}
              >
                klantenservice
              </a>{" "}
              als deze aangepast dienen te worden.
            </p>
          ),
          icon: Lock,
          variant: "muted" as const,
        }
      : showPayoutSetup
        ? {
            text: (
              <>
                Kies je uitbetaalfrequentie om uitbetalingen te ontvangen.{" "}
                <Link
                  to="/uitbetaling-instellen"
                  className="font-medium underline underline-offset-4 lg:hidden"
                >
                  Uitbetaling instellen
                </Link>
              </>
            ),
            icon: TriangleAlert,
            variant: "warning" as const,
          }
        : null;

  return (
    <div className="mx-auto max-w-6xl p-5 pt-10 lg:p-10">
      <div className="mb-8">
        <h1 className="text-3xl font-medium lg:text-4xl">Mijn gegevens</h1>
        <p className="text-muted-foreground mt-1.5 text-xs lg:text-sm">
          Bekijk en beheer je gegevens, machtiging en uitbetalingen.
        </p>
      </div>

      <div>
        <Section
          title="Profiel"
          action={<EditSectionActions edit={phoneEdit} />}
          notice={phoneNotice?.text}
          noticeIcon={phoneNotice?.icon}
          noticeVariant={phoneNotice?.variant}
        >
          <Row label="Voorletters" locked={phoneEdit.isEditing}>
            {val(p?.initials)}
          </Row>
          <Row label="Voornaam" locked={phoneEdit.isEditing}>
            {val(p?.firstName)}
          </Row>
          <Row label="Achternaam" locked={phoneEdit.isEditing}>
            {val(p?.lastName)}
          </Row>
          <Row label="Adres" locked={phoneEdit.isEditing}>
            {val(address)}
          </Row>
          <Row label="E-mailadres" locked={phoneEdit.isEditing}>
            {val(p?.email)}
          </Row>
          <Row label="Telefoonnummer">
            {phoneEdit.isEditing ? (
              <EditableFieldInput edit={phoneEdit} />
            ) : (
              formatPhone(p?.phoneNumber)
            )}
          </Row>
          <Row
            label="EAN-code"
            hint="Het EAN-nummer is de unieke code van je elektriciteitsaansluiting. Hiermee koppelen we je laadsessies aan de juiste locatie."
            locked={phoneEdit.isEditing}
          >
            {val(mandate?.eanCode)}
          </Row>
        </Section>

        <Section
          title="Machtiging"
          action={
            showSignCta && (
              <ActionButton variant="highlight" onClick={openMachtiging}>
                Onderteken machtiging
              </ActionButton>
            )
          }
          notice={
            !mandate ? (
              <>
                Onderteken je machtiging om uitbetalingen te ontvangen.{" "}
                {showSignCta && (
                  <Button
                    variant="link"
                    className="font-medium lg:hidden"
                    onClick={openMachtiging}
                  >
                    Onderteken machtiging
                  </Button>
                )}
              </>
            ) : undefined
          }
        >
          <Row label="Huidige machtiging">
            {mandateInForce ? (
              <Badge className="bg-status-success/15 text-status-success-strong hover:bg-status-success/15 gap-1.5 font-normal">
                <span className="bg-status-success size-1.5 rounded-full" />
                Actief
              </Badge>
            ) : mandateStartsNextYear ? (
              <Badge className="bg-status-warning/15 text-foreground hover:bg-status-warning/15 gap-1.5 font-normal">
                <span className="bg-status-warning size-1.5 rounded-full" />
                Start op 1 januari {futureStartYear}
              </Badge>
            ) : (
              <Badge className="bg-status-warning/15 text-foreground hover:bg-status-warning/15 gap-1.5 font-normal">
                <span className="bg-status-warning size-1.5 rounded-full" />
                {mandate ? "Verlopen" : "Nog geen machtiging ondertekend"}
              </Badge>
            )}
          </Row>
          {term && (
            <Row
              label="Looptijd"
              hint="De NEa werkt met kalenderjaren. Daarom loopt je machtiging altijd tot en met 31 december en stopt die daarna vanzelf."
            >
              {mandateInForce && upcomingTerm
                ? `Je huidige machtiging loopt af op ${term.endDate}. Je verlenging start op 1 januari ${futureStartYear} en loopt tot ${upcomingTerm.endDate}.`
                : mandateStartsNextYear && (upcomingTerm ?? term)
                  ? `Je machtiging start op 1 januari ${futureStartYear} en loopt tot ${(upcomingTerm ?? term)!.endDate}.`
                  : mandateInForce
                    ? `Je huidige machtiging loopt af op ${term.endDate}.`
                    : `Je machtiging is afgelopen op ${term.endDate}.`}
            </Row>
          )}
          {term?.commissionRate !== undefined && (
            <Row
              label="Servicekosten"
              hint={
                BRAND.volumeBenefit && term.volumeTiers?.length
                  ? `${VOLUME_DISCOUNT_INTRO} ${getVolumeSettlementNote(term.commissionRate)}`
                  : "Dit is wat wij vragen voor onze service. Daarvoor verzamelen, controleren en verkopen we de ERE's die jouw laadpaal oplevert."
              }
              action={
                BRAND.volumeBenefit && term.volumeTiers?.length ? (
                  <VolumeLadderDialog
                    baseRate={term.commissionRate}
                    tiers={term.volumeTiers}
                    kwh={ytdKwh}
                  >
                    <Button variant="outline" size="sm" className="rounded-full">
                      Bekijk staffel
                    </Button>
                  </VolumeLadderDialog>
                ) : undefined
              }
            >
              {BRAND.volumeBenefit && term.volumeTiers?.length ? (
                <div className="flex flex-col gap-1">
                  <VolumeTierStatus
                    baseRate={term.commissionRate}
                    tiers={term.volumeTiers}
                    ytdKwh={ytdKwh}
                  />
                </div>
              ) : (
                `${ratePercentages(term.commissionRate).commission}%`
              )}
            </Row>
          )}
          {pending && (
            <Row
              label="Ondertekening"
              hint="Een nieuwe looptijd start pas als je de machtiging hebt ondertekend. Tot die tijd blijft je huidige looptijd gelden."
              action={
                <ActionButton
                  variant="highlight"
                  onClick={() =>
                    openContractFlow({ step: "stap2", contractId: pending.contractId })
                  }
                >
                  Ondertekenen
                </ActionButton>
              }
            >
              {mandate ? "Je verlenging" : "Je machtiging"}
              {pendingTerm ? ` t/m ${pendingTerm.endYear}` : ""} wacht op je handtekening.
            </Row>
          )}
          {extend && (
            <Row
              label="Verlengen"
              hint={
                extend.kind === "extend"
                  ? "Hoe langer je machtiging, hoe lager je servicekosten. Verleng je tijdens het jaar? Dan geldt het nieuwe tarief voor het hele lopende kalenderjaar, ook voor kWh die je al hebt geladen."
                  : "Je machtiging is afgelopen. Je kunt opnieuw een looptijd kiezen."
              }
              action={
                <ActionButton variant="highlight" onClick={openExtendFlow}>
                  {extend.kind === "extend"
                    ? "Verleng machtiging"
                    : "Opnieuw ondertekenen"}
                </ActionButton>
              }
            >
              {extend.kind === "extend"
                ? "Verleng nu en profiteer van lagere servicekosten."
                : "Kies een nieuwe looptijd."}
            </Row>
          )}
          <Row label="Algemene voorwaarden">
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer noopener"
              className="text-foreground inline-flex items-center gap-1 font-medium underline underline-offset-4"
            >
              Bekijk de voorwaarden
              <ExternalLink className="size-3.5" />
            </a>
          </Row>
          {partner && (
            <Row label="Partnerintegratie">
              {partner.charAt(0).toUpperCase() + partner.slice(1)} actief
            </Row>
          )}
        </Section>

        <Section
          title="Uitbetalingen"
          action={
            // Je IBAN wijzigen kan pas als er een uitbetaalfrequentie staat.
            payoutLabel ? (
              <EditSectionActions edit={ibanEdit} />
            ) : showPayoutSetup ? (
              <ActionButton to="/uitbetaling-instellen">
                Uitbetaling instellen
              </ActionButton>
            ) : null
          }
          notice={ibanNotice?.text}
          noticeIcon={ibanNotice?.icon}
          noticeVariant={ibanNotice?.variant}
        >
          <Row label="Rekeninghouder" locked={ibanEdit.isEditing}>
            {val(fullName)}
          </Row>
          <Row label="IBAN-nummer">
            {ibanEdit.isEditing ? <EditableFieldInput edit={ibanEdit} /> : val(p?.iban)}
          </Row>
          {payoutLabel && (
            <Row label="Uitbetaalfrequentie" locked={ibanEdit.isEditing}>
              {payoutLabel}
            </Row>
          )}
        </Section>

        <CodeSection />
      </div>

      <p className="text-muted-foreground bg-muted/40 mt-6 rounded-md p-3 text-sm">
        Klopt er iets niet of heb je een vraag? Neem contact op met onze{" "}
        <a
          className="text-brand-darker cursor-pointer underline"
          onClick={() => window.$crisp?.push(["do", "chat:open"])}
        >
          klantenservice
        </a>
        .
      </p>
    </div>
  );
}
